/**
 * Plays a one-off confetti burst over the cart when an offer is earned.
 *
 * Fires every time the offer becomes applied — earn it, lose it, earn it again, and it plays
 * each time. What it does not do is replay while the offer simply stays applied, which would
 * otherwise happen on every quantity change, since the drawer re-renders through the Section
 * Rendering API. A flag in sessionStorage tracks the previous state so only the transition
 * counts.
 *
 * The artwork is a self-animating SMIL SVG served from theme assets: 30KB gzipped, no
 * JavaScript player, no WebAssembly and no third party CDN. It replaced a dotLottie player
 * that cost 555KB of WASM plus a 62KB module and took about three seconds to first paint.
 * It is still fetched only at the moment a celebration is due, never on page load.
 */

const STORAGE_KEY = 'bricks:offer-celebrated';

// One full pass of the animation, after which the overlay is torn down so it plays once
// rather than looping — the artwork itself repeats indefinitely.
const PLAY_MS = 5200;

/** @type {Promise<string> | null} */
let artwork = null;

/**
 * Fetches the artwork once and keeps the promise, so a later celebration reuses it.
 * @param {string | undefined} src
 * @returns {Promise<string> | null}
 */
function warm(src) {
  if (!src) return null;
  artwork ??= fetch(src)
    .then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.text();
    })
    .catch((error) => {
      artwork = null;
      throw error;
    });
  return artwork;
}

class OfferCelebration extends HTMLElement {
  async connectedCallback() {
    const earned = this.dataset.earned === 'true';

    let wasEarned = false;
    try {
      wasEarned = sessionStorage.getItem(STORAGE_KEY) === '1';
      sessionStorage.setItem(STORAGE_KEY, earned ? '1' : '0');
    } catch (_) {
      // Storage is blocked. Without it there is no way to tell a transition from a re-render,
      // so stay quiet rather than replaying on every cart update.
      return;
    }

    // Not applied: reset the flag, and warm the artwork if an offer is in progress so the
    // burst does not wait on a ~480ms fetch at the moment it lands.
    if (!earned) {
      if (this.dataset.prefetch === 'true') warm(this.dataset.src);
      return;
    }

    // Already applied before this render, so nothing changed — do not replay.
    if (wasEarned) return;

    const src = this.dataset.src;
    if (!src) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let markup;
    try {
      markup = await warm(src);
    } catch (_) {
      // The offer card still reads correctly without the flourish.
      return;
    }
    if (!markup) return;

    if (!this.isConnected) return;

    // Mounted on the body rather than anywhere inside the cart. The drawer's dialog slides in
    // on a transform, and a transformed ancestor becomes the containing block for position:
    // fixed, which would pin the burst inside the panel.

    const overlay = document.createElement('div');
    overlay.className = 'offer-celebration-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('popover', 'manual');
    overlay.innerHTML = markup;

    // The artwork is 1920x1080. Covering a tall phone screen with it means cropping the
    // sides, which is preferred over letterboxing it into a strip.
    overlay.querySelector('svg')?.setAttribute('preserveAspectRatio', 'xMidYMid slice');

    document.body.appendChild(overlay);

    // The open drawer is a modal dialog, so it lives in the top layer and would paint over a
    // plain body-level overlay however high its z-index. A manual popover joins the same top
    // layer without trapping focus or blocking clicks.
    try {
      overlay.showPopover();
    } catch (_) {
      // No popover support and no polyfill: it still covers the window on the cart page, and
      // sits behind the drawer rather than not showing at all.
    }

    // SMIL loops forever, so the overlay is what ends the celebration.
    setTimeout(() => overlay.remove(), PLAY_MS);
  }
}

if (!customElements.get('offer-celebration')) {
  customElements.define('offer-celebration', OfferCelebration);
}
